import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoaderData, useNavigate, useParams } from 'react-router-dom'

import { Breadcrumb } from '@/components/Breadcrumb.tsx'
import { StyledActionButton } from '@/components/Buttons.tsx'
import { CardRadio, CardRadioGroup } from '@/components/Radio.tsx'
import { Stepper } from '@/components/Stepper.tsx'
import {
  ButtonGroup,
  FullScreenContainer,
  ScrollableContent,
} from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { Header1, Subheader } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { calculateBMIPath, selectClinicPath } from '@/router/paths.ts'
import { useAppointmentStore } from '@/store.ts'
import { getAllowedTreatmentsVelthuis } from '@/utils/getAllowedTreatmentsVelthuis.ts'
import { showBMIForCategory } from '@/utils/showBMIForCategory.ts'

import { VelthuisFormStep } from '../../../utils/VelthuisFormSteps.ts'
import { LoaderData } from './actions.ts'

export const SelectAppointmentType = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'selectAppointment',
  })
  const navigate = useNavigate()
  const { category } = useParams() as {
    category: string
  }

  const {
    appointmentType,
    patientData: { dateOfBirth },
    setAppointmentType,
  } = useAppointmentStore((state) => ({
    appointmentType: state.appointmentType,
    patientData: state.patientData || {},
    setAppointmentType: state.setAppointmentType,
  }))
  const { appointmentTypes, loadTime } = useLoaderData() as LoaderData

  const { trackFormInteraction } = useTrackFormProgress(
    VelthuisFormStep.SelectAppointmentType,
    { loadTime },
  )

  const [displayedAppointmentTypes, setDisplayedAppointmentTypes] =
    useState(appointmentTypes)

  useEffect(() => {
    if (dateOfBirth) {
      const allowedAppointmentTypes = getAllowedTreatmentsVelthuis(dateOfBirth)

      setDisplayedAppointmentTypes(
        allowedAppointmentTypes
          ? appointmentTypes?.filter(
              (type) =>
                allowedAppointmentTypes === 'all' ||
                allowedAppointmentTypes?.includes(type.code),
            )
          : undefined,
      )
    }
  }, [dateOfBirth, appointmentTypes])

  return (
    <ScreenWrapper>
      <FullScreenContainer>
        <Breadcrumb
          onClick={() => {
            navigate('/')
            trackFormInteraction({
              action: 'previous',
              formFieldName: 'back-to-previous',
            })
          }}
          title={t('breadcrumbLink')}
        />
        <Header1>{t('appointmentType')}</Header1>
        <Stepper total={6} step={1} />
        <Subheader>{t('header')}</Subheader>
        <ScrollableContent>
          <CardRadioGroup
            name="appointmentType"
            id="appointmentType"
            value={appointmentType}
            onChange={(value: string) => {
              setAppointmentType(value)
              trackFormInteraction({
                action: 'click',
                formFieldName: 'appointmentType',
                formFieldValue: appointmentTypes?.find(
                  (category) => category.code === value,
                )?.display,
                formFieldValues: appointmentTypes?.map(
                  (category) => category.display,
                ),
              })
            }}
          >
            {displayedAppointmentTypes?.map((type) => (
              <CardRadio
                key={type.code}
                value={type.code}
                text={type.display}
              />
            ))}
          </CardRadioGroup>
        </ScrollableContent>
        <ButtonGroup>
          <StyledActionButton
            variant="Primary"
            type="submit"
            disabled={!appointmentType}
            onClick={() => {
              if (!appointmentType) return
              trackFormInteraction({
                action: 'next',
                formFieldName: 'submit',
              })
              if (showBMIForCategory(category)) {
                navigate(calculateBMIPath({ appointmentType, category }))
              } else {
                navigate(selectClinicPath({ appointmentType, category }))
              }
            }}
          >
            {t('actions.continue')}
          </StyledActionButton>
        </ButtonGroup>
      </FullScreenContainer>
    </ScreenWrapper>
  )
}
